// src/pages/GlobalSearch.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Search,
  Maximize,
  Minimize,
  Sparkles,
  Fingerprint,
  Zap,
  ShieldAlert,
  Loader2,
  X,
  ArrowRight,
  History,
  Bookmark,
  Check,
  Filter,
  ChevronDown,
  ChevronUp,
  Info,
  HelpCircle,
  Coins,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import DetailSidebar from "../components/DetailSidebar";
import SearchHistoryPanel from "../components/SearchHistoryPanel";
import { formatDistanceToNow } from "date-fns";
import FrostedModal from "../components/modals/FrostedModal";
import ExportCsvPanel from "../components/ExportCsvPanel";
import { MODAL_CONFIG } from "../utils/errors";
import { useLocation } from "react-router-dom";
import { redirectToLogin } from "../utils/auth";
import UnlockTokensBadge from "../components/UnlockTokensBadge";
import useUnlockTokens from "../hooks/useUnlockTokens";
import UpgradeCtaToast from "../components/UpgradeCtaToast";

/* ----------------------- helpers ----------------------- */
function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return fmtYMD(d);
};

function buildLogDateFilters(dateMode, range) {
  const filters = [];
  switch (dateMode) {
    case "3d":
      filters.push({ type: "logDate", value: { operator: ">=", date: daysAgo(3) } });
      break;
    case "7d":
      filters.push({ type: "logDate", value: { operator: ">=", date: daysAgo(7) } });
      break;
    case "30d":
      filters.push({ type: "logDate", value: { operator: ">=", date: daysAgo(30) } });
      break;
    case "range":
      if (range?.from) filters.push({ type: "logDate", value: { operator: ">=", date: range.from } });
      if (range?.to) filters.push({ type: "logDate", value: { operator: "<=", date: range.to } });
      break;
    case "all":
    default:
      break;
  }
  return filters;
}

const clearUrlParams = () => {
  const { protocol, host, pathname } = window.location;
  window.history.replaceState({}, "", `${protocol}//${host}${pathname}`);
};

const typeDefs = [
  { key: "consumerRecords", label: "Consumer Records",  description: "Returns exposed accounts that registered on the given domain — typically with personal email addresses (gmail, outlook, yahoo, etc.).", example: "acme.com" },
  { key: "corporateRecords", label: "Corporate Records", description: "Returns exposed accounts with a corporate email extension (e.g. user@acme.com).", example: "acme.com" },
  { key: "emailAddress", label: "Email Address",        description: "Returns every exposure tied to a specific mailbox — credential leaks, infostealer captures, and dark-web mentions.", example: "john@acme.com" },
  { key: "apkPackage", label: "APK Package",            description: "Returns infostealer captures associated with a specific Android app package.", example: "com.acme.app" },
  { key: "attackSurface", label: "Attack Surface",      description: "Returns exposed assets, subdomains, and services discovered for a given domain.", example: "acme.com" },
  { key: "ipAddress", label: "IP Address",              description: "Returns infostealer infections tied to a specific IPv4 or IPv6 address.", example: "8.8.8.8" },
  { key: "computerName", label: "Computer Name",        description: "Returns infostealer captures from a specific device hostname.", example: "DESKTOP-A1B2C3" },
];

// Map watchlist entry_type / recent query_type from the backend to a frontend
// searchType. Adjust if your backend uses different values, or if keyword/asn
// should route somewhere other than consumer records.
const ENTRY_TYPE_TO_SEARCH_TYPE = {
  domain: "consumerRecords",
  email: "emailAddress",
  ip: "ipAddress",
  keyword: "consumerRecords",
  asn: "attackSurface",
};

const KNOWN_SEARCH_TYPES = new Set([
  "consumerRecords",
  "corporateRecords",
  "emailAddress",
  "apkPackage",
  "attackSurface",
  "ipAddress",
  "computerName",
]);

// Resolve a searchType from a backend item. Checks the explicit search_type/
// searchType fields first; then accepts query_type/entry_type if they're
// already a known searchType (e.g. "corporateRecords"); else maps the
// value-class (e.g. "domain") via the table above.
const resolveSearchType = (item) => {
  const explicit = item?.search_type || item?.searchType;
  if (explicit && KNOWN_SEARCH_TYPES.has(explicit)) return explicit;
  const cls = item?.query_type || item?.entry_type;
  if (cls && KNOWN_SEARCH_TYPES.has(cls)) return cls;
  if (cls && ENTRY_TYPE_TO_SEARCH_TYPE[cls]) return ENTRY_TYPE_TO_SEARCH_TYPE[cls];
  return "consumerRecords";
};

const formatRelativeDate = (raw) => {
  try {
    // Backend emits UTC like "2026-04-27 13:42:11". Convert to ISO and append
    // Z so JS parses it as UTC, not browser-local. Skip the suffix if the
    // string already carries timezone info.
    const s = String(raw).replace(" ", "T");
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
    const d = new Date(hasTz ? s : s + "Z");
    if (isNaN(d.getTime())) return raw;
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return raw;
  }
};

function buildFilterArray(f) {
  const out = [];
  if (f?.subdomain?.trim()) {
    const v = f.subdomain.trim();
    out.push({ type: "subdomain", label: `Subdomain: ${v}`, value: v });
  }

    if (f?.event_date?.trim()) {
    const v = f.event_date.trim();
    out.push({ type: "event_date", label: `Event_Date: ${v}`, value: v });
  }
  if (f?.domain?.trim()) {
    const v = f.domain.trim();
    out.push({ type: "domain", label: `Domain: ${v}`, value: v });
  }
  if (f?.logType && f.logType !== "both") {
    out.push({ type: "breachType", label: `Type: ${f.logType}`, value: f.logType });
  }
  switch (f?.dateMode) {
    case "3d":
      out.push({ type: "date_last_n_days", label: "Last 3 days", value: 3 });
      break;
    case "7d":
      out.push({ type: "date_last_n_days", label: "Last 7 days", value: 7 });
      break;
    case "30d":
      out.push({ type: "date_last_n_days", label: "Last 30 days", value: 30 });
      break;
    case "range":
      if (f?.range?.from) out.push({ type: "date_from", label: `From: ${f.range.from}`, value: f.range.from });
      if (f?.range?.to) out.push({ type: "date_to", label: `To: ${f.range.to}`, value: f.range.to });
      break;
    case "all":
    default:
      break;
  }
  return out;
}

function downloadCSV(filename, rows) {
  if (!rows?.length) return;
  const headers = ["log_id", "username", "applications", "last_seen", "event_date", "data_type"];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [r.log_id, r.username, r.applications, r.last_seen || "", r.event_date || "", r.data_type]
        .map(escape)
        .join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function normalizeQueryAliases(input) {
  try {
    const url = new URL(input.includes("://") ? input : `https://${input}`);
    const host = url.hostname.toLowerCase();
    const map = { "x.com": "twitter.com", "www.x.com": "twitter.com" };
    if (map[host]) return input.replace(host, map[host]);
  } catch {
    const m = String(input).trim().toLowerCase();
    if (m === "x.com" || m === "www.x.com") return "twitter.com";
  }
  return input;
}

/* ----------------------- constants ----------------------- */
const MOBILE_CARD_WRAPPER = "mx-auto w-full max-w-[680px]"; // fixed/stable width on mobile

/* ----------------------- component ----------------------- */
export default function GlobalSearch() {
  const location = useLocation();
  const didHydrateFromUrl = useRef(false);



  // layout
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState("search");
  const { count, source, refresh } = useUnlockTokens({ refreshMs: 0 }); // manual refresh
  // search params
  const [query, setQuery] = useState("");
  const [finalQuery, setFinalQuery] = useState("");
  const [searchType, setSearchType] = useState("consumerRecords");
  // The searchType that was actually used by the last executed run.
  // Drifts from `searchType` when the user changes a chip without re-running.
  const [finalSearchType, setFinalSearchType] = useState("consumerRecords");

  // activity panel (recent / saved / watchlist)
  // Default state: open on desktop, closed on mobile (panel eats horizontal
  // space and pushes results off-screen on small viewports). Explicit user
  // toggles still win — sessionStorage preserves them for the current tab.
  const [historyPanelOpen, setHistoryPanelOpen] = useState(() => {
    try {
      const stored = sessionStorage.getItem("wi.historyPanelOpen");
      if (stored === "1") return true;
      if (stored === "0") return false;
      // No prior preference — default to closed so the search hero gets the
      // full width. Power users who toggle the panel open keep it open for
      // the session via sessionStorage.
      return false;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem("wi.historyPanelOpen", historyPanelOpen ? "1" : "0");
    } catch {}
  }, [historyPanelOpen]);
  useEffect(() => {
    const onKey = (e) => {
      const isToggle = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";
      if (isToggle) {
        e.preventDefault();
        setHistoryPanelOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // results & state
  const [results, setResults] = useState([]);
  const [resultsType, setResultsType] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Cross-dataset hint — backend returns `also_in: ["corporateRecords", …]`
  // listing other search types that have results for the same query. We
  // surface this as a small chip in the active-search banner so users don't
  // miss leaks sitting in a neighbouring dataset.
  const [alsoIn, setAlsoIn] = useState([]);
  const [dismissedHints, setDismissedHints] = useState(() => new Set());
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // export panel
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportLimit, setExportLimit] = useState(null);

  // details
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailInitialParams, setDetailInitialParams] = useState(null);



  const detailTypeMap = {
    consumerRecords: "consumerRecords",
    corporateRecords: "corporateRecords",
    apkPackage: "apkPackage",
    attackSurface: "attackSurface",
    emailAddress: "emailAddress",
    ipAddress: "ipAddress",
    computerName: "computerName",
  };

  // filters
  const defaultFilters = useRef({
    subdomain: "",
    domain: "",
    dateMode: "all",
    range: { from: "", to: "" },
    logType: "both",
    sortBy: "log_date", // "log_date" | "index_date"
  }).current;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [helpOpen, setHelpOpen] = useState(false);

  // Count of non-default filter values — shown as a badge on the search-bar
  // Filters button so users can see at a glance whether anything is set.
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.subdomain) n += 1;
    if (filters.domain) n += 1;
    if (filters.dateMode && filters.dateMode !== "all") n += 1;
    if (filters.logType && filters.logType !== "both") n += 1;
    if (filters.sortBy && filters.sortBy !== "log_date") n += 1;
    return n;
  }, [filters]);
  const sysMsgFetched = useRef(false);
  // modal
  const [modal, setModal] = useState({ open: false, type: null });
  const openModalFor = (type) => setModal({ open: true, type });
  const closeModal = () => setModal({ open: false, type: null });
  const cfg = modal.type ? MODAL_CONFIG[modal.type] : null;

  // concurrency guard
  const activeReq = useRef({ id: 0, controller: null });

  // UI text
  const placeholderMap = {
    consumerRecords: "Search consumer records… (e.g. acme.com)",
    corporateRecords: "Search corporate records… (e.g. acme.com)",
    apkPackage: "Search APK packages… (e.g. com.example.app)",
    attackSurface: "Search attack surface… (e.g. acme.com)",
    emailAddress: "Search by email address… (e.g. user@acme.com)",
    ipAddress: "Search by IP address or /24 subnet… (e.g. 192.168.1.1 or 192.168.1.0/24)",
    computerName: "Search by computer name… (e.g. DESKTOP-ABC123)",
    country: "Search by computer code (e.g. KP)",
    default: "Search records by type… (e.g. acme.com, user@example.com, 192.168.1.1)",

  };
  const placeholder = useMemo(
    () => placeholderMap[searchType] || placeholderMap.default,
    [searchType]
  );

  /* -------- which layout to show? -------- */
  const displayType = useMemo(() => (resultsType ? resultsType : searchType), [resultsType, searchType]);
const [isBannerVisible, setIsBannerVisible] = useState(true);
const showAIUpgradeBanner = useMemo(() => {
  return isBannerVisible && 
         displayType === "emailAddress" && 
         results.some(r => r.data_type === "stealer");
}, [results, displayType, isBannerVisible]);
  /* ------------------- filtering + sorting ------------------- */
  const filteredResults = useMemo(() => {
    if (!results?.length) return [];
    if (displayType === "attackSurface") return results;

    const withinDays = (isoDate, days) => {
      if (!isoDate) return false;
      const now = new Date();
      const d = new Date(isoDate + "T00:00:00Z");
      const diffDays = (now - d) / (1000 * 60 * 60 * 24);
      return diffDays <= days;
    };
    const withinRange = (isoDate, from, to) => {
      if (!isoDate) return false;
      const d = new Date(isoDate + "T00:00:00Z");
      const fromD = from ? new Date(from + "T00:00:00Z") : null;
      const toD = to ? new Date(to + "T00:00:00Z") : null;
      if (fromD && d < fromD) return false;
      if (toD && d > toD) return false;
      return true;
    };

    return results.filter((r) => {
      if (filters.logType !== "both" && r.data_type !== filters.logType) return false;
      if (filters.subdomain) {
        const needle = filters.subdomain.toLowerCase();
        const hay = (r.applications || "").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      const dateStr = r.last_seen || r.event_date;
      switch (filters.dateMode) {
        case "3d":
          return withinDays(dateStr, 3);
        case "7d":
          return withinDays(dateStr, 7);
        case "30d":
          return withinDays(dateStr, 30);
        case "range":
          return withinRange(dateStr, filters.range.from, filters.range.to);
        case "all":
        default:
          return true;
      }
    });
  }, [results, filters, displayType]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const sortedResults = useMemo(() => {
    if (displayType === "attackSurface") return filteredResults;
    let sortable = [...filteredResults];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = (a[sortConfig.key] ?? "").toString().toLowerCase();
        const bVal = (b[sortConfig.key] ?? "").toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredResults, sortConfig, displayType]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  /* ------------------- networking ------------------- */
  const runSearch = useCallback(
    async ({ q, pageNum, replace, typeOverride, filtersOverride }) => {
      const token = getJwtToken();
      const currentType = typeOverride || searchType;
      const currentFilters = filtersOverride || filters;

      const reqId = activeReq.current.id + 1;
      activeReq.current.id = reqId;

      if (activeReq.current.controller) activeReq.current.controller.abort();
      const controller = new AbortController();
      activeReq.current.controller = controller;

      setLoading(true);
      setErr("");
      setMessage("");

      setResultsType(currentType); // lock layout to this run

      const baseFilters = (buildFilterArray ? buildFilterArray(currentFilters) : []).filter(
        (f) => f?.type !== "logDate"
      );

      const dateFilters = buildLogDateFilters(currentFilters?.dateMode, currentFilters?.range);
      const finalFilters = [...baseFilters, ...dateFilters];
      const qNormalized = normalizeQueryAliases(q);
        
      try {
        const payload = {
          query: qNormalized,
          searchType: currentType,
          filters: finalFilters,
          page: pageNum,
          sort_by: currentFilters?.sortBy || "log_date",
        };

        const response = await fetch(
          "https://whiteintel.io/api/regular/app/v2/globalsearch/global_search_v6.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            cache: "no-store",
            signal: controller.signal,
            body: JSON.stringify(payload),
          }
        );

        if ([401, 419, 440].includes(response.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${response.status}`);
        }

        if (response.status === 403) {
          const text = await response.text();
          if (text.includes("Threat Intelligence license required")) {
            openModalFor("UPGRADE_REQUIRED");
          } else {
            openModalFor("FORBIDDEN_SEARCH");
          }
          setLoading(false);
          return;
        } else if (response.status === 400) {
          const text = await response.text();
          if (text.includes("Invalid email format")) {
            openModalFor("INVALID_EMAIL");
          }
          else if(text.includes("search limit"))
          {
            openModalFor("SEARCH_LIMIT");
          }
          else {
            openModalFor("GENERIC_ERROR");
          }
          setLoading(false);
          return;
        }

        if (!response.ok) {
          openModalFor("GENERIC_ERROR");
        }

        const data = await response.json();
        const newRows = Array.isArray(data?.results) ? data.results : [];
        if (pageNum === 1 && newRows.length === 0) {
          setMessage("No results found.");
        } else {
          setMessage(data?.message || "");
        }

        setResults((prev) => (replace ? newRows : [...prev, ...newRows]));
        setPage(pageNum);
        const inferredHasMore =
          typeof data?.has_more === "boolean" ? data.has_more : newRows.length > 0;
        setHasMore(inferredHasMore);

        // Capture cross-dataset hints only on the first page (page 2+ are
        // pagination requests for the same query, no need to refresh hints).
        // Backend currently sends per-type boolean flags (e.g. hasCorporateRecords);
        // we normalize them into an array of type keys so the renderer can
        // handle additional hints later without code changes.
        if (pageNum === 1) {
          const hints = [];
          if (data?.hasCorporateRecords === true) hints.push("corporateRecords");
          if (data?.hasConsumerRecords === true) hints.push("consumerRecords");
          if (data?.hasEmailAddress === true) hints.push("emailAddress");
          // Fallback: if the backend ever sends the older `also_in` array
          // shape, merge it in too.
          if (Array.isArray(data?.also_in)) {
            for (const t of data.also_in) {
              if (!hints.includes(t)) hints.push(t);
            }
          }
          setAlsoIn(hints);
        }
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error(e);
        setErr(e.message || "Search failed");
      } finally {
        if (activeReq.current.id === reqId) {
          setLoading(false);
          activeReq.current.controller = null;
        }
      }
    },
    [filters, searchType]
  );

  const [checkingLicense, setCheckingLicense] = useState(false);
const [showUpgradeModal, setShowUpgradeModal] = useState(false);

const handleExecuteAnalysis = async () => {
  if (checkingLicense) return;

  setCheckingLicense(true);
  try {
    const token = localStorage.getItem("jwtToken") || localStorage.getItem("token") || "";
    // We call your existing check_status or a dedicated /get_profile endpoint
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/check_access.php", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    });
    
    const json = await res.json();

    if (json.subscription === 'tifirm') {
      // User has the right license
      window.location.href = `/investigations?query=${encodeURIComponent(finalQuery)}`;
    } else {
      // Show the marketing modal for non-tifirm users
      setShowUpgradeModal(true);
    }
  } catch (err) {
    console.error("License check failed", err);
    // Fallback: Show modal if we can't confirm subscription
    setShowUpgradeModal(true);
  } finally {
    setCheckingLicense(false);
  }
};

  // Activity panel state — recent, saved, watchlist all fetched live.
  const [recentSearches, setRecentSearches] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState("");

  const [savedSearches, setSavedSearches] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState("");

  const [watchlistItems, setWatchlistItems] = useState([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [watchlistError, setWatchlistError] = useState("");

  // Auto-clear panel errors after 5s so they don't stick around forever.
  useEffect(() => {
    if (!recentError) return;
    const t = setTimeout(() => setRecentError(""), 5000);
    return () => clearTimeout(t);
  }, [recentError]);
  useEffect(() => {
    if (!savedError) return;
    const t = setTimeout(() => setSavedError(""), 5000);
    return () => clearTimeout(t);
  }, [savedError]);
  useEffect(() => {
    if (!watchlistError) return;
    const t = setTimeout(() => setWatchlistError(""), 5000);
    return () => clearTimeout(t);
  }, [watchlistError]);

  const fetchRecent = useCallback(async (signal) => {
    setRecentLoading(true);
    setRecentError("");
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/get_recent_searches.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({}),
          signal,
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data?.success) throw new Error("Bad response");
      const mapped = (data.searches || []).map((s, i) => ({
        id: `${s.query_type}:${s.query}:${s.call_date}:${i}`,
        query: s.query,
        type: s.query_type,
        searchType: resolveSearchType(s),
        at: formatRelativeDate(s.call_date),
      }));
      setRecentSearches(mapped);
    } catch (e) {
      if (e.name === "AbortError") return;
      setRecentError(e.message || "Failed to load");
    } finally {
      setRecentLoading(false);
    }
  }, []);

  const fetchSaved = useCallback(async (signal) => {
    setSavedLoading(true);
    setSavedError("");
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/saved_searches.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({ action: "list", page: 1 }),
          signal,
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data?.success) throw new Error("Bad response");
      const mapped = (data.searches || []).map((s) => {
        const st = resolveSearchType({ query_type: s.query_type });
        const typeLabel =
          typeDefs.find((t) => t.key === st)?.label || st;
        return {
          id: String(s.id),
          query: s.query,
          typeLabel,
          searchType: st,
          type: st,
        };
      });
      setSavedSearches(mapped);
    } catch (e) {
      if (e.name === "AbortError") return;
      setSavedError(e.message || "Failed to load");
    } finally {
      setSavedLoading(false);
    }
  }, []);

  const fetchWatchlist = useCallback(async (signal) => {
    setWatchlistLoading(true);
    setWatchlistError("");
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/get_watchlist_items.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({}),
          signal,
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data?.success) throw new Error("Bad response");
      const mapped = (data.entries || []).map((e, i) => ({
        id: `${e.entry_type}:${e.entry}:${i}`,
        label: e.entry,
        type: e.entry_type,
        searchType: resolveSearchType(e),
      }));
      setWatchlistItems(mapped);
    } catch (e) {
      if (e.name === "AbortError") return;
      setWatchlistError(e.message || "Failed to load");
    } finally {
      setWatchlistLoading(false);
    }
  }, []);

  // Refetch on every panel open — cheap (Redis-backed) and lets users
  // recover from transient errors by closing + reopening. Aborts in-flight
  // fetches if the user closes the panel mid-request.
  useEffect(() => {
    if (!historyPanelOpen) return;
    const ctrl = new AbortController();
    fetchRecent(ctrl.signal);
    fetchSaved(ctrl.signal);
    fetchWatchlist(ctrl.signal);
    return () => ctrl.abort();
  }, [historyPanelOpen, fetchRecent, fetchSaved, fetchWatchlist]);

  const handleSearch = useCallback((overrideType) => {
    const q = query.trim();
    // Allow callers (e.g. the "switch to Corporate Records" hint chip) to
    // override the search type without having to wait for the setSearchType
    // state update to flush. Plain Enter keypresses pass nothing and fall
    // back to the current dropdown selection.
    const effectiveType =
      typeof overrideType === "string" ? overrideType : searchType;

    setFinalQuery(q);
    setFinalSearchType(effectiveType);
    if (!q) return;

    setResults([]);
    setHasMore(false);
    setMessage("");
    setErr("");
    setPage(1);
    setHasSearched(true);
    setAlsoIn([]);
    clearUrlParams();
    setIsBannerVisible(true);
    setSaveBannerVisible(true);
    setRecentSearches((prev) => {
      const dedup = prev.filter(
        (r) => !(r.query === q && r.searchType === effectiveType)
      );
      return [
        {
          id: `r_${Date.now()}`,
          query: q,
          searchType: effectiveType,
          type: effectiveType,
          at: "just now",
        },
        ...dedup,
      ].slice(0, 50);
    });
    runSearch({ q, pageNum: 1, replace: true, typeOverride: effectiveType });
    // Sync recent from server — picks up whatever the backend recorded.
    // Optimistic prepend above gives instant feedback in case backend lags.
    if (historyPanelOpen) fetchRecent();
  }, [query, runSearch, searchType, historyPanelOpen, fetchRecent]);

  const handleHistorySelect = useCallback(
    (item) => {
      const q = String(item?.query || "").trim();
      if (!q) return;
      const nextType = item?.searchType || searchType;
      setQuery(q);
      setFinalQuery(q);
      setFinalSearchType(nextType);
      if (item?.searchType) setSearchType(item.searchType);
      setResults([]);
      setHasMore(false);
      setMessage("");
      setErr("");
      setPage(1);
      setHasSearched(true);
      clearUrlParams();
      setIsBannerVisible(true);
      setSaveBannerVisible(true);
      runSearch({ q, pageNum: 1, replace: true, typeOverride: nextType });
    },
    [runSearch, searchType]
  );

  // "Is the current search already in the saved list?" — match on query + type.
  const currentSavedEntry = useMemo(() => {
    const q = (finalQuery || "").trim();
    if (!q) return null;
    return (
      savedSearches.find(
        (s) => s.query === q && s.searchType === finalSearchType
      ) || null
    );
  }, [savedSearches, finalQuery, finalSearchType]);

  // Banner notice: "saved" | "removed" | null — auto-clears after 2s.
  // After a successful save, also auto-close the banner.
  const [saveNotice, setSaveNotice] = useState(null);
  const [saveBannerVisible, setSaveBannerVisible] = useState(true);
  useEffect(() => {
    if (!saveNotice) return;
    const wasSave = saveNotice === "saved";
    const t = setTimeout(() => {
      setSaveNotice(null);
      if (wasSave) setSaveBannerVisible(false);
    }, 2000);
    return () => clearTimeout(t);
  }, [saveNotice]);

  // When filter drawer opens, hide the activity panel so they don't stack.
  // Restore the panel's prior state when the filter closes.
  const historyPriorRef = useRef(false);
  useEffect(() => {
    if (isFilterOpen) {
      historyPriorRef.current = historyPanelOpen;
      if (historyPanelOpen) setHistoryPanelOpen(false);
    } else if (historyPriorRef.current) {
      setHistoryPanelOpen(true);
      historyPriorRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterOpen]);

  // Reverse direction: opening the activity panel closes the filter drawer.
  useEffect(() => {
    if (historyPanelOpen && isFilterOpen) {
      setIsFilterOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPanelOpen]);

  const handleSaveSearch = useCallback(async () => {
    const q = (finalQuery || "").trim();
    if (!q || currentSavedEntry) return;
    const tempId = `s_temp_${Date.now()}`;
    const typeLabel =
      typeDefs.find((t) => t.key === finalSearchType)?.label || finalSearchType;
    // Optimistic insert
    setSavedSearches((prev) => [
      {
        id: tempId,
        query: q,
        typeLabel,
        searchType: finalSearchType,
        type: finalSearchType,
      },
      ...prev,
    ]);
    setSaveNotice("saved");
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/saved_searches.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({
            action: "add",
            query: q,
            query_type: finalSearchType,
          }),
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      if (data.duplicate) {
        // Server already had it — pull authoritative list to align ids/order
        fetchSaved();
      } else if (data.id) {
        setSavedSearches((prev) =>
          prev.map((s) =>
            s.id === tempId ? { ...s, id: String(data.id) } : s
          )
        );
      }
    } catch (e) {
      // Roll back optimistic insert on failure
      setSavedSearches((prev) => prev.filter((s) => s.id !== tempId));
      setSavedError(e.message || "Save failed");
    }
  }, [finalQuery, currentSavedEntry, finalSearchType, fetchSaved]);

  const handleRemoveSaved = useCallback(async (id) => {
    let snapshot;
    setSavedSearches((prev) => {
      snapshot = prev;
      return prev.filter((s) => s.id !== id);
    });
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/saved_searches.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({ action: "delete", id: Number(id) }),
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      // Restore on failure
      if (snapshot) setSavedSearches(snapshot);
      setSavedError(e.message || "Delete failed");
    }
  }, []);

  const handleUnsaveCurrent = useCallback(() => {
    if (!currentSavedEntry) return;
    setSaveNotice("removed");
    handleRemoveSaved(currentSavedEntry.id);
  }, [currentSavedEntry, handleRemoveSaved]);

  const handleClearSaved = useCallback(async () => {
    let snapshot;
    setSavedSearches((prev) => {
      snapshot = prev;
      return [];
    });
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/saved_searches.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          cache: "no-store",
          body: JSON.stringify({ action: "clear" }),
        }
      );
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      // Restore on failure
      if (snapshot) setSavedSearches(snapshot);
      setSavedError(e.message || "Clear failed");
    }
  }, []);

  const handleViewMore = useCallback(() => {
    const q = query.trim();
    if (!q || loading) return;
    runSearch({ q, pageNum: page + 1, replace: false });
  }, [page, query, runSearch, loading]);

  /* ------------------- export (panel -> backend) ------------------- */
  async function handleExport(payload) {
    if (displayType === "attackSurface") {
      throw new Error("Export is not available for Attack Surface.");
    }

    const hasArr = Array.isArray(payload?.fields);
    const hasObj = payload?.selectedFields && typeof payload.selectedFields === "object";
    const pick = (keyArr, keyObj = keyArr) =>
      (hasArr && payload.fields.includes(keyArr)) ||
      (hasObj && !!payload.selectedFields[keyObj]);

    const selectedFields = {
      hostname: pick("hostname"),
      ipAddress: pick("ip_address", "ipAddress"),
      osInfo: pick("os_info", "osInfo"),
      antivirus: pick("antivirus"),
      malwarePath: pick("malware_path", "malwarePath"),
    };

    const hasTypesArr = Array.isArray(payload?.data_types);
    const hasTypesObj = payload?.selectedDataTypes && typeof payload.selectedDataTypes === "object";
    const typeOn = (arrVal, objKey) =>
      (hasTypesArr && payload.data_types.includes(arrVal)) ||
      (hasTypesObj && !!payload.selectedDataTypes[objKey]);

    const selectedDataTypes = {
      consumer: typeOn("consumerRecords", "consumer"),
      corporate: typeOn("corporateRecords", "corporate"),
    };

    const apiPayload = {
      query: (payload.query ?? payload.domain ?? query ?? "").trim(),
      start_date: payload.start_date ?? payload.startDate ?? "",
      end_date: payload.end_date ?? payload.endDate ?? "",
      selectedFields,
      selectedDataTypes,
      is_subdomain: !!payload.is_subdomain,
    };

    if (!apiPayload.query) throw new Error("Please provide a domain/query.");
    if (!apiPayload.selectedDataTypes.consumer && !apiPayload.selectedDataTypes.corporate) {
      throw new Error("Select at least one data type.");
    }

    const token = getJwtToken();
    if (!token) throw new Error("Authentication required.");

    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/globalsearch/get_download_as_csv_handler.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(apiPayload),
      }
    );

    if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }
    if (res.status === 403) {
      openModalFor("FORBIDDEN_SEARCH");
      return;
    }
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Export failed (${res.status})`);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export_${apiPayload.query}_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

// Sys-message fetch lives in <SysMessageGate /> at the App level now so it
// fires on whichever page the user lands on first after login, not just here.

  // fetch credits when panel opens
  useEffect(() => {
    if (!isExportOpen) return;
    (async () => {
      try {
        const token = getJwtToken();
        if (!token) {
          setExportLimit(0);
          return;
        }
        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/user/get_subscription.php",
          { method: "GET", headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setExportLimit(data?.success ? Number(data.csv_export_limit ?? 0) : 0);
      } catch (e) {
        console.error("Failed to fetch CSV export limit:", e);
        setExportLimit(0);
      }
    })();
  }, [isExportOpen]);

  /* ------------------- type switching & filters ------------------- */
  const handleSearchTypeChange = (newType) => {
    setSearchType(newType);
    setFilters(defaultFilters);
    setDraftFilters(defaultFilters);
    clearUrlParams();
  };

  const handleApplyFilters = () => {
    setFilters(draftFilters);
    setIsFilterOpen(false);
    const q = query.trim();
    if (!q) return;

    setResults([]);
    setPage(1);
    setHasMore(false);
    setMessage("");
    setErr("");
    runSearch({ q, pageNum: 1, replace: true, filtersOverride: draftFilters });
  };

  /* ------------------- URL hydration ------------------- */
  const mapUrlTypeToKey = useCallback((t) => {
    switch ((t || "").toLowerCase()) {
      case "consumer":
      case "consumerrecords":
        return "consumerRecords";
      case "corporate":
      case "corporaterecords":
      case "corporaterecords": // tolerate mixed cases
        return "corporateRecords";
      case "apk":
      case "apkpackage":
        return "apkPackage";
      case "surface":
      case "attacksurface":
        return "attackSurface";
      case "email":
      case "emailaddress":
        return "emailAddress";
      case "ip":
      case "ipaddress":
        return "ipAddress";
      case "computer":
      case "computername":
        return "computerName";
      default:
        return null;
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawType = params.get("type");
    const paramType = mapUrlTypeToKey(rawType);
    const paramQuery = (params.get("query") || "").trim();
    const paramSubdomain = (params.get("subdomain") || "").trim();
    const paramDomain = (params.get("domain") || "").trim();
    const paramEventDate = (params.get("event_date") || "").trim();

    // Only sync finalQuery FROM the URL when there's actually a query in the
    // URL — otherwise re-runs of this effect (triggered by runSearch changes
    // after a chip click) wipe finalQuery and unmount the banner.
    if (paramQuery) setFinalQuery(paramQuery);

    if (didHydrateFromUrl.current) return;
    if (!paramType && !paramQuery) return;

    didHydrateFromUrl.current = true;

    if (paramType) {
      setSearchType(paramType);
      setDetailInitialParams((prev) => ({
        ...(prev || {}),
        detail_type: paramType,
        query: paramQuery || prev?.query || "",
      }));
    }
    if (paramQuery) setQuery(paramQuery);

    const initialFilters = {
      ...defaultFilters,
      subdomain: paramSubdomain || "",
      domain: paramDomain || "",
      event_date: paramEventDate || ""
    };
    setFilters(initialFilters);

    setDraftFilters(initialFilters);

    if (paramQuery && paramType) {
      setResults([]);
      setHasMore(false);
      setMessage("");
      setErr("");
      setHasSearched(true);
      runSearch({
        q: paramQuery,
        pageNum: 1,
        replace: true,
        typeOverride: paramType,
        filtersOverride: initialFilters,
      });
    }
  }, [location.search, mapUrlTypeToKey, runSearch, defaultFilters]);

  /* ------------------- UI helpers ------------------- */
  const activeChips = useMemo(() => {
    if (displayType === "attackSurface") return [];
    const chips = [];
    if (filters.subdomain) chips.push({ key: "subdomain", label: `Subdomain: ${filters.subdomain}` });
    if (filters.domain) chips.push({ key: "domain", label: `Domain: ${filters.domain}` });
    if (filters.event_date) chips.push({ key: "event_date", label: `Event Date: ${filters.event_date}` });
    // Log Type is rendered as its own dedicated dropdown chip (always visible);
    // skip it here so we don't double-render.
    if (filters.dateMode === "3d") chips.push({ key: "dateMode", label: "Last 3 days" });
    if (filters.dateMode === "7d") chips.push({ key: "dateMode", label: "Last 7 days" });
    if (filters.dateMode === "30d") chips.push({ key: "dateMode", label: "Last 30 days" });
    if (filters.dateMode === "range") {
      chips.push({
        key: "dateMode",
        label: `Date: ${filters.range.from || "—"} → ${filters.range.to || "—"}`,
      });
    }
    // Sort By is rendered as its own dedicated dropdown chip; skip here.
    return chips;
  }, [filters, displayType]);

  // Unified chip clear — used by both toolbar chips and the banner chips.
  // Patches filter state AND re-runs the search with the new filters so the
  // user sees fresh results immediately.
  // Routes through a ref so it always calls the freshest runSearch (avoids
  // any stale-closure issues if the function is rebuilt between renders).
  const runSearchRef = useRef(runSearch);
  useEffect(() => {
    runSearchRef.current = runSearch;
  }, [runSearch]);

  // Patch the sortBy filter and re-run. Used by the Sort By dropdown chip.
  const applySortBy = useCallback(
    (value) => {
      const next = { ...filters, sortBy: value };
      setFilters(next);
      setDraftFilters((d) => ({ ...d, sortBy: value }));
      const q = (finalQuery || query || "").trim();
      if (!q) return;
      setResults([]);
      setHasMore(false);
      setMessage("");
      setErr("");
      setPage(1);
      runSearchRef.current({
        q,
        pageNum: 1,
        replace: true,
        filtersOverride: next,
      });
    },
    [filters, finalQuery, query]
  );

  // Patch the logType filter and re-run the search. Used by the dedicated
  // Log Type dropdown chip in the toolbar.
  const applyLogType = useCallback(
    (value) => {
      const next = { ...filters, logType: value };
      setFilters(next);
      // Mirror the change into draftFilters so an open filter drawer reflects
      // it without overriding any other in-progress edits the user has made.
      setDraftFilters((d) => ({ ...d, logType: value }));
      const q = (finalQuery || query || "").trim();
      if (!q) return;
      setResults([]);
      setHasMore(false);
      setMessage("");
      setErr("");
      setPage(1);
      runSearchRef.current({
        q,
        pageNum: 1,
        replace: true,
        filtersOverride: next,
      });
    },
    [filters, finalQuery, query]
  );

  const clearChip = useCallback(
    (key) => {
      const patch = {};
      if (key === "subdomain") patch.subdomain = "";
      if (key === "domain") patch.domain = "";
      if (key === "event_date") patch.event_date = "";
      if (key === "logType") patch.logType = "both";
      if (key === "dateMode") {
        patch.dateMode = "all";
        patch.range = { from: "", to: "" };
      }
      if (key === "sortBy") patch.sortBy = "log_date";
      const next = { ...filters, ...patch };
      setFilters(next);
      // Mirror into draftFilters so an open filter drawer reflects the change
      // without clobbering other in-progress edits.
      setDraftFilters((d) => ({ ...d, ...patch }));
      const q = (finalQuery || query || "").trim();
      if (!q) return;
      setResults([]);
      setHasMore(false);
      setMessage("");
      setErr("");
      setPage(1);
      runSearchRef.current({ q, pageNum: 1, replace: true, filtersOverride: next });
    },
    [filters, finalQuery, query]
  );

  const handleExportCSV = useCallback(() => {
    downloadCSV(
      `whiteintel_global_search_${query || "export"}_${new Date().toISOString().slice(0, 10)}.csv`,
      filteredResults
    );
  }, [filteredResults, query]);

  const frosted =
    "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-sm shadow-[0_0_10px_rgba(56,189,248,0.05)]";
  const chip =
    "inline-flex items-center rounded-full px-3 py-1.5 text-sm transition-colors";

  // detail opener
const handleView = (row) => {
    // 1. Determine the query to use right now
    // If finalQuery is empty, we use 'query', otherwise we use 'finalQuery'
    const effectiveQuery = (finalQuery || query || "").trim();

    // 2. Sync the state for future use if it's currently empty
    if (!finalQuery && query) {
      setFinalQuery(query);
    }

    const isAttackSurface = displayType === "attackSurface";
    
    const body = isAttackSurface
      ? {
          id: Number(row?.id),
          type: "attackSurface",
          detail_type: "attackSurface",
          query: effectiveQuery, // Use the fallback variable
        }
      : {
          id: Number(row?.log_id),
          type: String(row?.data_type || "stealer"),
          detail_type: detailTypeMap[displayType] || "consumerRecords",
          query: effectiveQuery, // Use the fallback variable
        };

    setDetailRow(row);
    setDetailInitialParams(body);
    setDetailOpen(true);
  };

  const handleViewAttackSurface = useCallback(
    (row) => {
      const mcRaw = String(row?.data_type || "").trim(); // match_condition (e.g. "*.acme.com" or "*@acme.com")
      const app = String(row?.application || "").trim();

      let typeParam = "consumerRecords";
      let domain = "";

      if (mcRaw.includes("@")) {
        typeParam = "corporateRecords";
        const m = mcRaw.match(/@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/);
        domain = (m?.[1] || "").toLowerCase();
      } else {
        typeParam = "consumerRecords";
        domain = mcRaw
          .replace(/^\*\.\s*/i, "")
          .replace(/^\*\s*/i, "")
          .replace(/^\./, "")
          .toLowerCase();
      }

      if (!domain) domain = (finalQuery || "").trim();

      const qs = new URLSearchParams();
      qs.set("type", typeParam);
      qs.set("query", domain);

      if (typeParam === "consumerRecords" && app) {
        qs.set("subdomain", app);
      }
      if (typeParam === "corporateRecords" && domain) {
        qs.set("domain", domain); // FIX: use registrable domain, not application
      }

      const href = `/search?${qs.toString()}`;
      window.open(href, "_blank", "noopener,noreferrer");
    },
    [query]
  );

  /* ------------------- render ------------------- */
  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Upgrade CTA — backend decides visibility (free plan + low tokens).
          We pass the unlock-token `count` as refreshKey so the toast
          re-checks status the moment a user runs an unlock and depletes
          their balance. `historyPanelOpen` shifts the toast left when the
          activity panel is open so they don't visually overlap. */}
      <UpgradeCtaToast refreshKey={count ?? 0} shiftLeft={historyPanelOpen} />

      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              Global Search
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Help button — first-timer onboarding modal. Experienced users
                skip it, which is fine. Sits to the left of the Unlock Tokens
                chip so it's visible the moment a new user lands on the page. */}
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-sky-900/40 bg-neutral-900/40 px-2.5 py-1 text-[11px] md:text-[12px] text-neutral-300 hover:text-sky-200 hover:border-sky-500/40 transition-colors"
              aria-label="Open Whiteintel help"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              <span>Need help?</span>
            </button>

            <span
              className={`inline-flex items-center gap-1.5 rounded-full ${frosted} ring-1 ring-sky-500/30 px-2.5 py-1 text-[11px] md:text-[12px] text-sky-100`}
            >
              <Coins className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Unlock Tokens:</span>
              <span className="tabular-nums font-bold">{count ?? "—"}</span>
            </span>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey={active}
          onChange={setActive}
        />

       <main
  className={`flex-1 min-w-0 transition-[padding] duration-200 ease-out
              ${mobileOpen ? "ml-0" : "ml-0"} ${collapsed ? "md:ml-10" : "md:ml-0"}
              ${isFilterOpen && searchType !== "attackSurface" ? "lg:pr-[340px]" : ""}
              ${historyPanelOpen ? "lg:pr-[340px]" : ""}`}
>
          {/* Hero + XL search */}
          <section className="px-3 md:px-6 pt-4 md:pt-8">
            <div className="mx-auto max-w-6xl text-center">
              <h2 className="text-[18px] md:text-[36px] font-bold leading-tight">
                Search in 40M+ Devices and 10B+ Accounts
              </h2>

{showUpgradeModal && (
  <div 
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[6px] animate-in fade-in duration-300"
    onClick={() => setShowUpgradeModal(false)}
  >
    {/* Increased max-width to 2xl for a wider aspect ratio */}
    <div 
      className="relative w-full max-w-2xl rounded-md border border-white/10 bg-[#000414] shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden animate-in zoom-in-95 duration-300"
      onClick={(e) => e.stopPropagation()}
    >
      
      {/* Close Button */}
      <button 
        onClick={() => setShowUpgradeModal(false)}
        className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/20 text-neutral-500 hover:text-white hover:bg-white/10 transition-all border border-white/5"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Condensed Visual Header */}
      <div 
        className="h-24 bg-gradient-to-br from-sky-500/20 to-transparent flex items-center justify-center relative overflow-hidden"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cpath d='M0 40 L40 40 L40 0' fill='none' stroke='white' stroke-opacity='0.05' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-sky-500/10 to-transparent h-full w-full animate-[scan_4s_linear_infinite] opacity-30" />
        
        <div className="relative flex items-center gap-4 px-8 w-full">
           <div className="w-12 h-12 rounded-md bg-[#000414] border border-sky-400/30 flex items-center justify-center shadow-lg">
             <Sparkles className="w-6 h-6 text-sky-400 animate-pulse" />
           </div>
           <div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
                <span className="text-[10px] font-black text-sky-400 uppercase tracking-[0.3em]">Whiteintel AI Assistant</span>
              </div>
              <h3 className="text-xl font-black text-white uppercase tracking-tight">Forensic Identity Attribution</h3>
           </div>
        </div>
      </div>

      <div className="px-8 py-6">
        <div className="mb-6">
          <p className="text-sm text-neutral-400 leading-relaxed">
            Leverage autonomous heuristics to de-anonymize targets and map lateral exposure. 
            <span className="block mt-1 text-sky-400 font-semibold italic text-xs">
              Threat Intel license is required for Whiteintel AI Assistant.
            </span>
          </p>
        </div>

        {/* Intelligence Features - Compact Row/Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {[
            { 
              icon: <Fingerprint className="w-4 h-4 text-sky-400" />, 
              title: "Identity Correlation", 
              desc: "Resolve real names, phone numbers & Govt IDs." 
            },
            { 
              icon: <Zap className="w-4 h-4 text-amber-400" />, 
              title: "Neural Scoring", 
              desc: "Prioritize critical assets via AI prioritization." 
            },
            { 
              icon: <ShieldAlert className="w-4 h-4 text-emerald-400" />, 
              title: "Footprinting", 
              desc: "Trace historical identity exposure across logs." 
            }
          ].map((feat, i) => (
            <div key={i} className="flex flex-col gap-2 p-3.5 rounded-md bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors group">
              <div className="flex items-center gap-2">
                {feat.icon}
                <p className="text-[10px] font-black text-sky-100 uppercase tracking-widest">{feat.title}</p>
              </div>
              <p className="text-[11px] text-neutral-500 leading-snug">{feat.desc}</p>
            </div>
          ))}
        </div>

        {/* Actions - Horizontal Layout */}
        <div className="flex flex-col md:flex-row items-center gap-4">
          <button 
            onClick={() => window.location.href = '/settings/billing'}
            className="w-full md:flex-1 py-3.5 rounded-md bg-sky-500 hover:bg-sky-400 text-white font-black text-xs tracking-[0.2em] transition-all shadow-xl shadow-sky-500/30 uppercase active:scale-[0.98]"
          >
            Upgrade to Threat Intel
          </button>
          <button 
            onClick={() => setShowUpgradeModal(false)}
            className="w-full md:w-auto px-6 py-2 text-neutral-600 hover:text-neutral-400 text-[10px] font-black uppercase tracking-[0.3em] transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
)}


              <div className="mt-3 md:mt-4 mx-auto max-w-[1100px]">
                <div className="flex items-stretch gap-2">
                  {/* Activity — flanks the search bar on the left. Same height
                      as the bar but visibly secondary (narrower, ghost
                      styling). Lets users load a saved/recent search BEFORE
                      they type anything. */}
                  <button
                    type="button"
                    onClick={() => setHistoryPanelOpen((v) => !v)}
                    title="Recent, saved searches & watchlist (Ctrl+K)"
                    aria-label="Toggle activity panel"
                    aria-pressed={historyPanelOpen}
                    className={`hidden md:inline-flex shrink-0 items-center gap-2 rounded-lg ${frosted} border px-3 transition-colors ${
                      historyPanelOpen
                        ? "border-sky-400/60 bg-sky-500/[0.08] text-sky-200"
                        : "border-sky-900/40 text-neutral-300 hover:text-white hover:bg-white/[0.04]"
                    }`}
                  >
                    <History className="h-4 w-4" />
                    <span className="text-[12px] font-medium">Activity</span>
                  </button>
                  {/* Compound search bar: type selector on the left,
                      magnifier + input on the right. Single bordered shell
                      so the two read as one control. No overflow-hidden here
                      — the type selector's dropdown panel needs to escape
                      the bar's bounds. */}
                  <div className={`relative flex-1 hidden md:flex items-stretch rounded-lg ${frosted} border border-sky-600/50 focus-within:ring-2 focus-within:ring-sky-500/60`}>
                    <SearchTypeSelector
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      types={typeDefs}
                    />
                    <div className="relative flex-1">
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        placeholder={placeholder}
                        className="w-full bg-transparent pl-11 pr-4 py-2.5 md:py-3.5 text-[15px] md:text-[18px] placeholder-neutral-500 outline-none"
                        inputMode="search"
                        aria-label="Global search input"
                      />
                      <svg
                        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-300/80"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 2a8 8 0 105.293 14.293l4.147 4.147a1 1 0 001.414-1.414l-4.147-4.147A8 8 0 0010 2zm-6 8a6 6 0 1112 0 6 6 0 01-12 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Mobile-only: type selector lives in the dropdown below;
                      this is just the search input. */}
                  <div className={`relative flex-1 md:hidden`}>
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      placeholder={placeholder}
                      className={`w-full rounded-md ${frosted} border border-sky-600/50 pl-12 pr-3 py-3 text-[16px] placeholder-neutral-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60`}
                      inputMode="search"
                      aria-label="Global search input"
                    />
                    <svg
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-sky-300"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 2a8 8 0 105.293 14.293l4.147 4.147a1 1 0 001.414-1.414l-4.147-4.147A8 8 0 0010 2zm-6 8a6 6 0 1112 0 6 6 0 01-12 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  {/* Filters — flanks the search bar on the right, same
                      treatment as Activity. Lets users prep filters BEFORE
                      executing the initial search. Disabled until there's a
                      query to apply them to. */}
                  {searchType !== "attackSurface" && (() => {
                    const canFilter = query.trim().length > 0;
                    return (
                      <button
                        type="button"
                        onClick={() => {
                          setDraftFilters(filters);
                          setIsFilterOpen(true);
                        }}
                        disabled={!canFilter}
                        title={canFilter ? "Apply filters" : "Type a query first to apply filters"}
                        aria-label="Open filters"
                        aria-pressed={isFilterOpen}
                        className={`hidden md:inline-flex shrink-0 relative items-center gap-2 rounded-lg ${frosted} border px-3 transition-colors ${
                          !canFilter
                            ? "border-sky-900/40 text-neutral-500 opacity-60 cursor-not-allowed"
                            : activeFilterCount > 0
                            ? "border-sky-400/60 bg-sky-500/[0.08] text-sky-200"
                            : "border-sky-900/40 text-neutral-300 hover:text-white hover:bg-white/[0.04]"
                        }`}
                      >
                        <Filter className="h-4 w-4" />
                        <span className="text-[12px] font-medium">Filters</span>
                        {activeFilterCount > 0 && (
                          <span
                            className="inline-flex items-center justify-center rounded-full bg-sky-500/20 border border-sky-500/40 text-sky-100 text-[10px] font-mono px-1.5 py-px"
                            aria-label={`${activeFilterCount} active filters`}
                          >
                            {activeFilterCount}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* Mobile-only: type dropdown + Activity/Filters row */}
              <div className="md:hidden mt-2 mx-auto max-w-[1100px] space-y-2">
                <div className="text-left">
                  <label className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1 block">
                    Type
                  </label>
                  <div className="relative">
                    <select
                      value={searchType}
                      onChange={(e) => handleSearchTypeChange(e.target.value)}
                      className={`w-full rounded-md ${frosted} border border-sky-600/50 pl-3 pr-10 py-2.5 text-[15px] text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60 appearance-none`}
                      aria-label="Search type"
                    >
                      {typeDefs.map((t) => (
                        <option key={t.key} value={t.key} className="bg-[#000414]">
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-300"
                      aria-hidden="true"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setHistoryPanelOpen((v) => !v)}
                    aria-label="Toggle activity panel"
                    aria-pressed={historyPanelOpen}
                    className={`flex-1 inline-flex items-center justify-center gap-2 rounded-md ${frosted} border ${
                      historyPanelOpen
                        ? "border-sky-400/70 ring-1 ring-sky-400/30"
                        : "border-sky-600/50"
                    } px-3 py-2.5 text-sky-200 hover:bg-white/[0.04] transition-colors`}
                  >
                    <History className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wider font-medium">
                      Activity
                    </span>
                  </button>
                  {searchType !== "attackSurface" && (() => {
                    const canFilter = query.trim().length > 0;
                    return (
                      <button
                        type="button"
                        onClick={() => {
                          setDraftFilters(filters);
                          setIsFilterOpen(true);
                        }}
                        disabled={!canFilter}
                        aria-label="Open filters"
                        className={`flex-1 relative inline-flex items-center justify-center gap-2 rounded-md ${frosted} border ${
                          activeFilterCount > 0
                            ? "border-sky-400/70 ring-1 ring-sky-400/30"
                            : "border-sky-600/50"
                        } px-3 py-2.5 transition-colors ${
                          canFilter
                            ? "text-sky-200 hover:bg-white/[0.04]"
                            : "text-neutral-500 opacity-50 cursor-not-allowed"
                        }`}
                      >
                        <Filter className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider font-medium">
                          Filters
                        </span>
                        {activeFilterCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-sky-500 text-white text-[11px] font-semibold flex items-center justify-center ring-2 ring-[#000414]">
                            {activeFilterCount}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* (Type chips removed — the search type is now picked inline
                  from the left side of the search bar itself.) */}

              {/* Primary search action on mobile */}
              <div className="mt-3 md:hidden">
                <button
                  onClick={handleSearch}
                  className="w-full rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-base text-sky-200 hover:bg-sky-500/20"
                >
                  Search
                </button>
              </div>

              {/* Active-search banner: shows the executed query + filters, lets user save */}
              {hasSearched && (finalQuery || "").trim() && saveBannerVisible && (
                <div className="mt-3 md:mt-4 mx-auto max-w-[1100px] text-left">
                  <div className={`rounded-md ${frosted} px-3 md:px-4 py-2 md:py-2.5 flex flex-wrap items-center gap-2`}>
                    <span className="text-[10px] md:text-[11px] uppercase tracking-wider text-neutral-500">
                      Active
                    </span>
                    <span className="text-sm md:text-base text-white font-medium truncate max-w-[40%]">
                      &ldquo;{finalQuery}&rdquo;
                    </span>
                    <span className="rounded-full bg-sky-500/10 text-sky-300 text-[10px] md:text-[11px] px-2 py-0.5 ring-1 ring-sky-500/20">
                      {typeDefs.find((t) => t.key === finalSearchType)?.label || finalSearchType}
                    </span>

                    {/* Cross-dataset hint — only shown for Consumer → Corporate
                        suggestions (analyst searched consumer leaks, query
                        also has corporate matches). Dismissible per session so
                        the same query+type pair won't re-prompt.
                        We gate on BOTH the executed type AND the currently-
                        selected type: switching the dropdown to Corporate
                        before re-running should hide the hint immediately,
                        otherwise it sits there pointing at where the user
                        already intends to go. */}
                    {finalSearchType === "consumerRecords" &&
                      searchType === "consumerRecords" &&
                      alsoIn.includes("corporateRecords") &&
                      !dismissedHints.has(`${finalQuery}::corporateRecords`) && (
                        <span className="inline-flex items-center gap-2 rounded-md border border-sky-900/50 bg-neutral-900/40 pl-2.5 pr-1 py-1 text-[11px]">
                          <Info className="h-3 w-3 text-sky-400/70 shrink-0" />
                          <button
                            type="button"
                            onClick={() => {
                              setQuery(finalQuery);
                              // Keep the dropdown in sync with the new
                              // executed type, but pass the override directly
                              // to handleSearch so we don't depend on the
                              // setState flush before the request fires.
                              handleSearchTypeChange("corporateRecords");
                              handleSearch("corporateRecords");
                            }}
                            className="inline-flex items-center gap-1.5 font-medium text-sky-300 hover:text-sky-200 transition-colors"
                            title={`Re-run "${finalQuery}" in Corporate Records`}
                          >
                            Corporate leak records exist for this query
                            <span aria-hidden="true">→</span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setDismissedHints((prev) => {
                                const next = new Set(prev);
                                next.add(`${finalQuery}::corporateRecords`);
                                return next;
                              })
                            }
                            className="ml-0.5 p-0.5 rounded text-neutral-600 hover:text-neutral-300 hover:bg-white/[0.04] transition-colors"
                            title="Dismiss hint"
                            aria-label="Dismiss Corporate Records suggestion"
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </span>
                      )}

                    {/* Active filter chips removed from this banner — they're
                        already rendered in the toolbar chip row above the
                        results, and duplicating them here was crowding the
                        cross-dataset hint. Banner stays focused on:
                        Active · "<query>" · <type> · [Corporate hint]. */}

                    <div className="flex-1" />

                    {(() => {
                      // Three states: just-saved / just-removed / steady
                      if (saveNotice === "saved") {
                        return (
                          <span className="inline-flex items-center gap-1.5 rounded-md text-[11px] md:text-xs font-medium px-2.5 py-1 bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30">
                            <Check className="h-3.5 w-3.5" />
                            Saved
                          </span>
                        );
                      }
                      if (saveNotice === "removed") {
                        return (
                          <span className="inline-flex items-center gap-1.5 rounded-md text-[11px] md:text-xs font-medium px-2.5 py-1 bg-neutral-500/10 text-neutral-300 ring-1 ring-neutral-500/30">
                            <X className="h-3.5 w-3.5" />
                            Removed
                          </span>
                        );
                      }
                      if (currentSavedEntry) {
                        return (
                          <button
                            type="button"
                            onClick={handleUnsaveCurrent}
                            title="Remove from saved"
                            className="inline-flex items-center gap-1.5 rounded-md text-[11px] md:text-xs font-medium px-2.5 py-1 bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-rose-500/15 hover:text-rose-200 hover:ring-rose-500/40 transition-colors"
                          >
                            <Bookmark className="h-3.5 w-3.5 fill-current" />
                            Saved
                          </button>
                        );
                      }
                      return (
                        <button
                          type="button"
                          onClick={handleSaveSearch}
                          className="inline-flex items-center gap-1.5 rounded-md text-[11px] md:text-xs font-medium px-2.5 py-1 bg-sky-500/10 text-sky-200 ring-1 ring-sky-500/30 hover:bg-sky-500/20 hover:text-white transition-colors"
                        >
                          <Bookmark className="h-3.5 w-3.5" />
                          Save search
                        </button>
                      );
                    })()}

                    <button
                      type="button"
                      onClick={() => setSaveBannerVisible(false)}
                      title="Dismiss"
                      aria-label="Dismiss active search banner"
                      className="ml-1 p-1 rounded text-neutral-500 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>

                    {(() => {
                      const queryChanged =
                        query.trim() !== (finalQuery || "").trim();
                      const typeChanged = searchType !== finalSearchType;
                      if (!queryChanged && !typeChanged) return null;
                      const newTypeLabel =
                        typeDefs.find((t) => t.key === searchType)?.label ||
                        searchType;
                      const msg = typeChanged
                        ? `Press Enter to re-run with ${newTypeLabel}`
                        : "Press Enter to apply";
                      return (
                        <div className="basis-full mt-1.5 pt-1.5 border-t border-white/5 text-[11px] md:text-xs text-amber-300/90 flex items-center gap-1.5">
                          <kbd className="px-1 py-0 rounded bg-white/5 border border-white/10 text-amber-200/90 text-[10px]">
                            ⏎
                          </kbd>
                          <span>{msg}</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Results + toolbar + table/cards */}
          <section className="px-3 md:px-6 py-4 md:py-8">
            <div className="mx-auto max-w-7xl">
              {(() => {
                const hasQuery = (finalQuery || "").trim().length > 0;
                const shouldShowPanel = hasQuery || loading || !!err || (results?.length ?? 0) > 0;
                if (!shouldShowPanel) return <div />;

                return (
                  <>

{/* Results + toolbar + table/cards */}
{showAIUpgradeBanner && !loading && (
  <div className="group relative mb-4 py-2 md:py-2.5 px-4 md:px-5 rounded-md border border-sky-500/20 bg-[#000414] overflow-hidden transition-all hover:border-sky-500/40 shadow-md">
    {/* Minimalist Background Glow */}
    <div className="absolute -top-12 -right-12 w-32 h-32 bg-sky-500/5 blur-[50px] pointer-events-none" />

    {/* Dismiss Button - Vertically Centered */}
    <button
      onClick={() => setIsBannerVisible(false)}
      className="absolute top-1/2 -translate-y-1/2 right-2 md:right-3 z-20 p-1.5 rounded-md text-neutral-600 hover:text-white hover:bg-white/5 transition-all"
      title="Dismiss"
    >
      <X className="w-3.5 h-3.5" />
    </button>

    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-3 md:pr-8">
      <div className="flex items-center gap-3 md:gap-4 w-full overflow-hidden">
        {/* Compact AI Icon */}
        <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-lg bg-sky-500/10 flex items-center justify-center border border-sky-500/20">
          <Sparkles className="w-4 h-4 md:w-5 h-5 text-sky-400" />
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-4 overflow-hidden">
          {/* Label + Badge (Badge hidden on mobile) */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <h4 className="text-[11px] md:text-sm font-bold text-white whitespace-nowrap tracking-tight">
              AI Analyst Available
            </h4>
            <span className="hidden md:inline-block px-1.5 py-0.5 rounded bg-sky-500/10 border border-sky-500/20 text-[8px] font-black uppercase text-sky-400 tracking-tighter">
              Whiteintel AI
            </span>
          </div>

          {/* Responsive Description */}
          <p className="text-[10px] md:text-xs text-neutral-400 whitespace-nowrap overflow-hidden">
            {/* 1. Large Desktop Version */}
            <span className="hidden xl:inline">
              Execute automated forensic mapping of connected identities and
              lateral exposure with{" "}
              <span className="text-neutral-200 font-medium">
                precise reports
              </span>
              .
            </span>

            {/* 2. Tablet Version */}
            <span className="hidden md:inline xl:hidden">
              Map identities and lateral exposure with{" "}
              <span className="text-neutral-200 font-medium">
                precise reports
              </span>
              .
            </span>

            {/* 3. Ultra-Condensed Mobile Version */}
            <span className="md:hidden">Conduct precise analysis.</span>
          </p>
        </div>
      </div>

      {/* Action Button */}
<button
  onClick={handleExecuteAnalysis}
  disabled={checkingLicense}
  className="w-full md:w-auto whitespace-nowrap px-4 md:px-5 py-1.5 md:py-2 rounded-lg bg-sky-500 hover:bg-sky-400 disabled:bg-sky-500/50 text-white font-bold text-[10px] md:text-xs transition-all flex items-center justify-center gap-2 group/btn shadow-lg"
>
  {checkingLicense ? (
    <>
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      Verifying License...
    </>
  ) : (
    <>
      Execute Investigate+
      <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
    </>
  )}
</button>
    </div>
  </div>
)}
                    <div className="md:mb-3 mb-2 md:static sticky top-0 z-10 bg-[#000414]/90 backdrop-blur px-2 md:px-0">
                      <div className="flex items-center justify-between py-2 md:py-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Always-visible Log Type & Sort By dropdown chips */}
                          {displayType !== "attackSurface" && (
                            <div className="hidden md:inline-flex items-center gap-2">
                              <LogTypeChip
                                value={filters.logType}
                                onChange={applyLogType}
                                searchType={searchType}
                              />
                              <SortByChip
                                value={filters.sortBy}
                                onChange={applySortBy}
                              />
                              {/* Secondary entry to the filter drawer — sits
                                  inline with the chips for users who want to
                                  add filters after seeing initial results. */}
                              {(() => {
                                const canFilter = (finalQuery || query || "").trim().length > 0;
                                return (
                                  <button
                                    type="button"
                                    disabled={!canFilter}
                                    onClick={() => {
                                      setDraftFilters(filters);
                                      setIsFilterOpen(true);
                                    }}
                                    title={
                                      canFilter
                                        ? "Open more filter options"
                                        : "Run a search first"
                                    }
                                    className={`text-xs underline underline-offset-2 transition-colors ${
                                      canFilter
                                        ? "text-sky-300 hover:text-sky-100 cursor-pointer"
                                        : "text-neutral-600 cursor-not-allowed"
                                    }`}
                                  >
                                    + More filters
                                  </button>
                                );
                              })()}
                            </div>
                          )}

                          {/* Active chips */}
                          {displayType !== "attackSurface" && activeChips.length > 0 && (
                            <div className="hidden md:flex flex-wrap items-center gap-2">
                              {activeChips.map((c) => (
                                <span
                                  key={c.key + c.label}
                                  className="inline-flex items-center gap-2 rounded-full border border-neutral-700 px-3 py-1.5 text-xs text-neutral-200"
                                >
                                  {c.label}
                                  <button
                                    onClick={() => clearChip(c.key)}
                                    className="text-neutral-400 hover:text-neutral-200"
                                    title="Clear"
                                    aria-label={`Clear ${c.label}`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                              <button
                                onClick={() => {
                                  setFilters(defaultFilters);
                                  setDraftFilters(defaultFilters);
                                }}
                                className="text-xs text-neutral-400 underline hover:text-neutral-200"
                              >
                                Clear all
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
{!["ipAddress", "attackSurface", "computerName"].includes(displayType) && (
  <button
    type="button"
    onClick={() => setIsExportOpen(true)}
    disabled={!filteredResults.length}
    className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
  >
    Export CSV
  </button>
)}
                        </div>
                      </div>

                      {/* Chips visible on mobile under toolbar */}
                      {displayType !== "attackSurface" && (
                        <div className="md:hidden pb-2 flex flex-wrap items-center gap-2">
                          <LogTypeChip
                            value={filters.logType}
                            onChange={applyLogType}
                            searchType={searchType}
                          />
                          <SortByChip
                            value={filters.sortBy}
                            onChange={applySortBy}
                          />
                          {(() => {
                            const canFilter = (finalQuery || query || "").trim().length > 0;
                            return (
                              <button
                                type="button"
                                disabled={!canFilter}
                                onClick={() => {
                                  setDraftFilters(filters);
                                  setIsFilterOpen(true);
                                }}
                                className={`text-[11px] underline underline-offset-2 transition-colors ${
                                  canFilter
                                    ? "text-sky-300 hover:text-sky-100"
                                    : "text-neutral-600"
                                }`}
                              >
                                + More filters
                              </button>
                            );
                          })()}
                          {activeChips.map((c) => (
                            <span
                              key={c.key + c.label}
                              className="inline-flex items-center gap-2 rounded-full border border-neutral-700 px-3 py-1.5 text-[11px] text-neutral-200"
                            >
                              {c.label}
                              <button
                                onClick={() => clearChip(c.key)}
                                className="text-neutral-400 hover:text-neutral-200"
                                title="Clear"
                                aria-label={`Clear ${c.label}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={() => {
                              setFilters(defaultFilters);
                              setDraftFilters(defaultFilters);
                            }}
                            className="text-[11px] text-neutral-400 underline hover:text-neutral-200"
                          >
                            Clear all
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Errors only — server "success" messages are noise and
                        were sitting above the table for no reason. */}
                    {err && (
                      <div className="mb-2 text-[11px] md:text-xs text-red-400">
                        Error: {err}
                      </div>
                    )}

                    {/* Results container */}
                    <div className="rounded-md border border-sky-900/50 bg-gradient-to-b from-[#000414] to-[#06101e] shadow-[0_0_16px_rgba(56,189,248,0.1)]">
                      {/* Attack Surface: cards on mobile, table on desktop */}
                      {displayType === "attackSurface" ? (
                        <div className="p-2 md:p-0">
                          <div className={`md:hidden ${MOBILE_CARD_WRAPPER}`}>
                            <AttackSurfaceCards
                              rows={results}
                              loading={loading}
                              err={err}
                              hasQuery={hasSearched}
                              onView={handleViewAttackSurface}
                            />
                          </div>
                          <div className="hidden md:block overflow-auto">
                            <AttackSurfaceTable
                              rows={results}
                              loading={loading}
                              err={err}
                              hasQuery={hasSearched}
                              onView={handleViewAttackSurface}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* mobile: cards (fixed width wrapper) */}
                          <div className={`md:hidden p-2 ${MOBILE_CARD_WRAPPER}`}>
                            <MobileResultCards
                              rows={sortedResults}
                              loading={loading}
                              err={err}
                              hasQuery={hasSearched}
                              onView={handleView}
                              logType={filters.logType}
                              onResetLogType={() => applyLogType("both")}
                            />
                          </div>

                          {/* desktop: table — enterprise density (~32-36px row
                              height, 13px cells, 10px uppercase headers,
                              sky-tinted hairlines, muted type chips). */}
                          <div className="hidden md:block overflow-auto">
                            <table className="w-full table-fixed">
                              <thead className="bg-[#0a1224]/95 sticky top-0">
                                <tr className="border-b border-sky-900/30">
                                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-24">Id</th>
                                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-64">Leaked accounts</th>
                                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-64">Applications</th>
                                  <th
                                    onClick={() => requestSort("data_type")}
                                    className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 cursor-pointer select-none hover:text-neutral-300 transition-colors w-32"
                                    aria-sort={
                                      sortConfig?.key === "data_type"
                                        ? sortConfig.direction === "asc"
                                          ? "ascending"
                                          : "descending"
                                        : "none"
                                    }
                                  >
                                    <span className="inline-flex items-center gap-1">
                                      Type
                                      {sortConfig?.key === "data_type" &&
                                        (sortConfig.direction === "asc" ? (
                                          <ChevronUp className="h-3 w-3 text-neutral-400" />
                                        ) : (
                                          <ChevronDown className="h-3 w-3 text-neutral-400" />
                                        ))}
                                    </span>
                                  </th>
                                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-40">Last seen</th>
                                  {/* Action column header intentionally blank — enterprise convention. */}
                                  <th className="px-4 py-2 w-24" aria-label="Actions" />
                                </tr>
                              </thead>

                              <tbody className="divide-y divide-sky-900/30">
                                {sortedResults.length === 0 ? (
                                  loading ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                      <TableRowSkeleton key={i} />
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={6} className="px-4 py-10 text-center text-neutral-500 text-[13px]">
                                        {err ? (
                                          "Failed to load results."
                                        ) : !hasSearched ? (
                                          "No results yet. Try searching."
                                        ) : (
                                          <div className="space-y-2">
                                            <div>No results found.</div>
                                            {filters.logType && filters.logType !== "both" && (
                                              <div className="text-[12px] text-neutral-500">
                                                Tip:{" "}
                                                <button
                                                  type="button"
                                                  onClick={() => applyLogType("both")}
                                                  className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline"
                                                >
                                                  switch Log Type to All
                                                </button>{" "}
                                                to broaden this search.
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                ) : (
                                  sortedResults.map((r, idx) => (
                                    <tr key={`${r.log_id}-${idx}`} className="hover:bg-white/[0.02] transition-colors">
                                      <td className="px-4 py-2.5 align-middle text-[12px] text-neutral-500 font-mono tabular-nums truncate" title={r.log_id}>
                                        {r.log_id}
                                      </td>
                                      <td className="px-4 py-2.5 align-middle text-[13px] text-white font-mono truncate" title={r.username}>
                                        {r.username}
                                      </td>
                                      <td className="px-4 py-2.5 align-middle text-[13px] text-neutral-300 truncate" title={r.applications}>
                                        {r.applications}
                                      </td>
                                      <td className="px-4 py-2.5 align-middle">
                                        <span
                                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono font-semibold ${
                                            r.data_type === "stealer"
                                              ? "bg-rose-500/[0.08] text-rose-300 border-rose-500/30"
                                              : r.data_type === "database"
                                              ? "bg-violet-500/[0.08] text-violet-300 border-violet-500/30"
                                              : "bg-amber-500/[0.08] text-amber-300 border-amber-500/30"
                                          }`}
                                        >
                                          {r.data_type}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2.5 align-middle text-[12px] text-neutral-400 font-mono tabular-nums truncate" title={r.last_seen || "—"}>
                                        {r.last_seen || "—"}
                                      </td>
                                      <td className="px-4 py-2.5 align-middle text-right">
                                        <button
                                          onClick={() => handleView(r)}
                                          className="inline-flex items-center gap-1 text-[12px] text-sky-300 hover:text-sky-200 transition-colors"
                                          aria-label="View details"
                                        >
                                          View
                                          <ArrowRight className="h-3 w-3" />
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Load more — compact, ghost styling, matches the rest
                        of the app's secondary button system. */}
                    <div className="mt-3 md:mt-4 flex justify-center">
                      <button
                        onClick={handleViewMore}
                        disabled={!hasMore || loading || !results.length}
                        className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 bg-neutral-900/40 px-3 py-1.5 text-[12px] text-neutral-300 hover:text-white hover:bg-neutral-900 disabled:opacity-40 disabled:hover:bg-neutral-900/40 disabled:hover:text-neutral-300 transition-colors"
                      >
                        {loading ? "Loading…" : hasMore ? "Load more" : "No more results"}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </section>

          {/* Side panel: Filters (hidden when selected type is Attack Surface).
              On lg+ this sits beside the main column (no backdrop) so the user
              can see results while tweaking; on smaller screens it overlays. */}
          {isFilterOpen && searchType !== "attackSurface" && (
            <>
              {/* Backdrop only on small screens — lg+ keeps content interactive */}
              <div
                className="fixed inset-0 z-[55] bg-black/60 lg:hidden"
                onClick={() => setIsFilterOpen(false)}
              />
              <div
                className="fixed right-0 top-0 h-full w-full md:w-[340px] z-[60] flex flex-col border-l border-sky-900/40 bg-[#0a1224]"
                role="dialog"
                aria-label="Filters"
              >
                {/* Header — uppercase tracked, no big icon, lucide X close. */}
                <div className="flex items-center justify-between border-b border-sky-900/30 px-4 py-3">
                  <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-neutral-200">
                    Filters
                  </h3>
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-white/[0.04] transition-colors"
                    aria-label="Close filters"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                  {(() => {
                    const showSubdomain = searchType === "consumerRecords";
                    const showDomain = searchType === "corporateRecords";

                    // Shared classes — kept inline so the drawer doesn't need
                    // to import from another file just for these.
                    const sectionLabel = "block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-2";
                    const inputCls = "w-full rounded-md border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-[13px] text-white outline-none focus:border-sky-500/50 transition-colors";
                    const toggleBtn = (isActive) =>
                      `inline-flex items-center rounded-md px-2.5 py-1 text-[12px] border transition-colors ${
                        isActive
                          ? "border-sky-500/40 bg-sky-500/10 text-sky-200"
                          : "border-sky-900/30 bg-neutral-900/40 text-neutral-300 hover:text-white hover:border-sky-700/50"
                      }`;

                    return (
                      <>
                        {(showSubdomain || showDomain) && (
                          <section className="px-4 py-4 border-b border-sky-900/20">
                            <label className={sectionLabel}>
                              {showSubdomain ? "Subdomain" : "Domain"}
                            </label>
                            <input
                              type="text"
                              placeholder={showSubdomain ? "e.g. admin.whiteintel.io" : "e.g. office365.com"}
                              value={(showSubdomain ? draftFilters.subdomain : draftFilters.domain) || ""}
                              onChange={(e) =>
                                setDraftFilters((f) => ({
                                  ...f,
                                  [showSubdomain ? "subdomain" : "domain"]: e.target.value,
                                }))
                              }
                              className={inputCls}
                            />
                          </section>
                        )}

                        {/* Date */}
                        <section className="px-4 py-4 border-b border-sky-900/20">
                          <div className={sectionLabel}>Date range</div>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              { key: "3d",    label: "Last 3d" },
                              { key: "7d",    label: "Last 7d" },
                              { key: "30d",   label: "Last 30d" },
                              { key: "all",   label: "All time" },
                              { key: "range", label: "Custom" },
                            ].map((d) => {
                              const isActive = draftFilters.dateMode === d.key;
                              return (
                                <button
                                  key={d.key}
                                  type="button"
                                  onClick={() =>
                                    setDraftFilters((f) => ({
                                      ...f,
                                      dateMode: d.key,
                                      range: d.key === "range" ? f.range || { from: "", to: "" } : { from: "", to: "" },
                                    }))
                                  }
                                  className={toggleBtn(isActive)}
                                  aria-pressed={isActive}
                                >
                                  {d.label}
                                </button>
                              );
                            })}
                          </div>

                          {draftFilters.dateMode === "range" && (
                            <div className="mt-3 flex items-center gap-2">
                              <input
                                type="date"
                                value={draftFilters.range?.from || ""}
                                onChange={(e) =>
                                  setDraftFilters((f) => ({
                                    ...f,
                                    range: { ...(f.range || {}), from: e.target.value },
                                  }))
                                }
                                className={inputCls}
                              />
                              <span className="text-neutral-500 text-[11px] shrink-0">to</span>
                              <input
                                type="date"
                                value={draftFilters.range?.to || ""}
                                onChange={(e) =>
                                  setDraftFilters((f) => ({
                                    ...f,
                                    range: { ...(f.range || {}), to: e.target.value },
                                  }))
                                }
                                className={inputCls}
                              />
                            </div>
                          )}
                        </section>

                        {/* Log Type */}
                        <section className="px-4 py-4 border-b border-sky-900/20">
                          <div className={sectionLabel}>Log type</div>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              { key: "both",      label: "All" },
                              { key: "stealer",   label: "Stealer" },
                              { key: "combolist", label: "Combolist" },
                              // "Database" only applies when searching Corporate Records.
                              ...(searchType === "corporateRecords"
                                ? [{ key: "database", label: "Database" }]
                                : []),
                            ].map((o) => {
                              const isActive = (draftFilters.logType || "both") === o.key;
                              return (
                                <button
                                  key={o.key}
                                  type="button"
                                  onClick={() => setDraftFilters((f) => ({ ...f, logType: o.key }))}
                                  className={toggleBtn(isActive)}
                                  aria-pressed={isActive}
                                >
                                  {o.label}
                                </button>
                              );
                            })}
                          </div>
                        </section>

                        {/* Sort By stays in the toolbar chip row — it's a
                            persistent post-search modifier, not a filter. */}
                      </>
                    );
                  })()}
                </div>

                {/* Footer — solid primary CTA, ghost cancel, plain reset link. */}
                <div className="sticky bottom-0 border-t border-sky-900/30 bg-[#0a1224] px-4 py-3">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setDraftFilters(defaultFilters)}
                      type="button"
                      className="text-[12px] text-neutral-500 hover:text-neutral-200 transition-colors"
                    >
                      Reset
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsFilterOpen(false)}
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 bg-transparent px-3 py-1.5 text-[13px] text-neutral-300 hover:bg-white/5 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleApplyFilters}
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 transition-colors"
                      >
                        Apply filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Export CSV Panel */}
          <ExportCsvPanel
            open={isExportOpen}
            onClose={() => setIsExportOpen(false)}
            credits={exportLimit ?? 0}
            onExport={handleExport}
            query={query}
          />

          {/* Detail sidebar */}
          <DetailSidebar
            open={detailOpen}
            row={detailRow}
            initialParams={detailInitialParams}
            jwtToken={getJwtToken()}
            onClose={() => setDetailOpen(false)}
             onUnlocked={refresh}
          />

          {/* Activity panel: recent searches, saved searches, watchlist */}
          <SearchHistoryPanel
            open={historyPanelOpen}
            onClose={() => setHistoryPanelOpen(false)}
            onSelect={handleHistorySelect}
            recent={recentSearches}
            recentLoading={recentLoading}
            recentError={recentError}
            onDismissRecentError={() => setRecentError("")}
            saved={savedSearches}
            savedLoading={savedLoading}
            savedError={savedError}
            onDismissSavedError={() => setSavedError("")}
            watchlist={watchlistItems}
            watchlistLoading={watchlistLoading}
            watchlistError={watchlistError}
            onDismissWatchlistError={() => setWatchlistError("")}
            onRemoveSaved={handleRemoveSaved}
            onClearSaved={handleClearSaved}
            activeQuery={finalQuery}
            activeSearchType={finalSearchType}
          />

          {/* Frosted modal */}
          {cfg && (
            <FrostedModal
              open={modal.open}
              onClose={closeModal}
              icon={cfg.icon}
              title={cfg.title}
              description={cfg.description}
              primaryAction={
                cfg.primary && {
                  label: cfg.primary.label,
                  onClick: () => {
                    if (cfg.primary.href) window.location.href = cfg.primary.href;
                    else closeModal();
                  },
                }
              }
              secondaryAction={
                cfg.secondary && {
                  label: cfg.secondary.label,
                  onClick: closeModal,
                }
              }
            />
          )}

          {/* Help / orientation modal — opened by the "Need help?" button
              in the header. First-timer onboarding: explains what the tool
              does, how search types work, what unlock tokens are, and where
              to upgrade. */}
          <FrostedModal
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            eyebrow="Whiteintel · Quick guide"
            icon={<HelpCircle className="h-5 w-5 text-sky-400" />}
            title="How Whiteintel Search works"
            maxWidthClass="max-w-[760px]"
            description={
              <>
                <p className="text-neutral-300">
                  Whiteintel scans dark-web forums, infostealer logs, and leaked databases for any identifier you care about — email, domain, IP, computer name, and more.
                </p>

                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1.5">
                    Search types
                  </div>
                  <p className="text-[13px] text-neutral-400 leading-relaxed">
                    Pick a dataset from the dropdown on the left of the search bar — each one scopes your query to a specific data class. Switch the type to broaden or narrow your investigation.
                  </p>
                  {/* Pull descriptions straight from typeDefs so the modal and
                      the in-bar dropdown can never drift apart. */}
                  <ul className="mt-3 space-y-2 rounded-md border border-sky-900/40 bg-neutral-900/30 divide-y divide-sky-900/30">
                    {typeDefs.map((t) => (
                      <li key={t.key} className="px-3 py-2.5">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                          <div className="text-[13px] font-semibold text-white">
                            {t.label}
                          </div>
                          {t.example && (
                            <div className="inline-flex items-center gap-1.5 text-[11px] text-neutral-500">
                              <span className="uppercase tracking-wider text-[9px] text-neutral-500 font-semibold">Example</span>
                              <code className="font-mono text-sky-300 bg-sky-500/[0.06] border border-sky-900/40 rounded px-1.5 py-0.5">
                                {t.example}
                              </code>
                            </div>
                          )}
                        </div>
                        {t.description && (
                          <div className="mt-1 text-[12px] text-neutral-400 leading-snug">
                            {t.description}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1.5">
                    Unlock tokens
                  </div>
                  <p className="text-[13px] text-neutral-400 leading-relaxed">
                    Every search returns previews for free. Spending one unlock token reveals the full record — credentials, host metadata, capture history, and downloadable archives where available. Your remaining tokens are shown in the chip to the right of this button.
                  </p>
                </div>

                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1.5">
                    Going further
                  </div>
                  <p className="text-[13px] text-neutral-400 leading-relaxed">
                    Paid plans give you unlimited unlocks, REST API access, more watchlisted identifiers, and managed takedowns for impersonating domains.
                  </p>
                </div>
              </>
            }
            primaryAction={{
              label: "View plans",
              onClick: () => {
                setHelpOpen(false);
                window.location.href = "/upgrades";
              },
            }}
            secondaryAction={{
              label: "Close",
              onClick: () => setHelpOpen(false),
            }}
          />
        </main>
      </div>
    </div>
  );
}

/* ------------------- log-type dropdown chip ------------------- */
/* ------------------- inline search-type selector ------------------- */
// Lives on the left side of the search bar — the same pattern Splunk,
// GitHub Code Search, Kibana, and Linear all use. Each option carries a
// short description so users immediately see what each dataset covers.
function SearchTypeSelector({ value, onChange, types }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 320 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const current = types.find((t) => t.key === value) || types[0];

  // Position the dropdown right below the button. Re-runs on scroll/resize
  // so it stays anchored even if the page scrolls while the dropdown is open.
  const refreshCoords = () => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(320, r.width) });
  };

  useEffect(() => {
    if (!open) return;
    refreshCoords();
    const onDocClick = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inButton && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", refreshCoords);
    window.addEventListener("scroll", refreshCoords, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", refreshCoords);
      window.removeEventListener("scroll", refreshCoords, true);
    };
  }, [open]);

  return (
    <div className="shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-full inline-flex items-center gap-2.5 px-3.5 md:px-4 text-left border-r border-sky-900/40 hover:bg-white/[0.04] focus-visible:outline-none focus-visible:bg-white/[0.04] transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change search type"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-medium">
            Search type
          </div>
          <div className="text-[13px] md:text-[14px] font-semibold text-white truncate">
            {current.label}
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-neutral-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown lives in a portal so it escapes the search-bar's stacking
          context (the bar uses `backdrop-blur` which creates one). Otherwise
          the active-search banner / filter pills below would render on top. */}
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: coords.top, left: coords.left, width: coords.width }}
          className="fixed z-[200] rounded-md bg-[#000414] border border-sky-900/40 shadow-2xl py-1 max-h-[80vh] overflow-auto"
          role="listbox"
        >
          {types.map((t) => {
            const isSelected = t.key === value;
            return (
              <button
                key={t.key}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(t.key);
                  setOpen(false);
                }}
                className={`block w-full text-left px-4 py-2.5 transition-colors ${
                  isSelected ? "bg-sky-500/10" : "hover:bg-white/[0.04]"
                }`}
              >
                <div className={`text-[13px] font-semibold ${isSelected ? "text-sky-300" : "text-white"}`}>
                  {t.label}
                </div>
                {t.description && (
                  <div className="mt-0.5 text-[11px] text-neutral-500 leading-snug">
                    {t.description}
                  </div>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

function LogTypeChip({ value, onChange, searchType }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 180 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const refreshCoords = () => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(180, r.width) });
  };

  useEffect(() => {
    if (!open) return;
    refreshCoords();
    const onDocClick = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inButton && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", refreshCoords);
    window.addEventListener("scroll", refreshCoords, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", refreshCoords);
      window.removeEventListener("scroll", refreshCoords, true);
    };
  }, [open]);

  // "Database" is a Corporate-Records-only data source — it doesn't apply to
  // consumer or other search types, so we only surface it conditionally.
  const isCorporate = searchType === "corporateRecords";

  const labelMap = {
    both: "All",
    stealer: "Stealer",
    combolist: "Combolist",
    ...(isCorporate ? { database: "Database" } : {}),
  };
  const current = value || "both";

  return (
    <div className="relative inline-flex">
      <span className="inline-flex items-center rounded-full border border-neutral-700 text-xs text-neutral-200 overflow-hidden">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 hover:bg-white/[0.04] transition-colors"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="text-neutral-400">Log Type:</span>
          <span className="text-neutral-100">{labelMap[current]}</span>
          <ChevronDown
            className={`h-3 w-3 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {current !== "both" && (
          <button
            type="button"
            onClick={() => onChange("both")}
            className="text-neutral-400 hover:text-neutral-200 px-2 py-1.5 border-l border-neutral-700"
            title="Reset"
            aria-label="Reset log type"
          >
            ×
          </button>
        )}
      </span>
      {/* Dropdown portaled to document.body so it escapes any ancestor's
          stacking context (sticky toolbar, backdrop-blur containers, etc.)
          and renders above the results table. */}
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: coords.top, left: coords.left, minWidth: coords.width }}
          className="fixed z-[200] rounded-md bg-[#000414] border border-sky-900/40 shadow-2xl py-1"
          role="listbox"
        >
          {[
            { key: "both", label: "All" },
            { key: "stealer", label: "Stealer" },
            { key: "combolist", label: "Combolist" },
            ...(isCorporate ? [{ key: "database", label: "Database" }] : []),
          ].map((opt) => {
            const selected = current === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
                className={`block w-full text-left px-3 py-1.5 text-sm transition-colors ${
                  selected
                    ? "text-sky-300 bg-sky-500/10"
                    : "text-neutral-200 hover:bg-white/[0.04]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ------------------- sort-by dropdown chip ------------------- */
function SortByChip({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 160 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const refreshCoords = () => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(160, r.width) });
  };

  useEffect(() => {
    if (!open) return;
    refreshCoords();
    const onDocClick = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inButton && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", refreshCoords);
    window.addEventListener("scroll", refreshCoords, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", refreshCoords);
      window.removeEventListener("scroll", refreshCoords, true);
    };
  }, [open]);

  const labelMap = {
    log_date: "Log Date",
    index_date: "Index Date",
  };
  const current = value || "log_date";

  return (
    <div className="relative inline-flex">
      <span className="inline-flex items-center rounded-full border border-neutral-700 text-xs text-neutral-200 overflow-hidden">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 hover:bg-white/[0.04] transition-colors"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="text-neutral-400">Sort By:</span>
          <span className="text-neutral-100">{labelMap[current]}</span>
          <ChevronDown
            className={`h-3 w-3 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {current !== "log_date" && (
          <button
            type="button"
            onClick={() => onChange("log_date")}
            className="text-neutral-400 hover:text-neutral-200 px-2 py-1.5 border-l border-neutral-700"
            title="Reset"
            aria-label="Reset sort"
          >
            ×
          </button>
        )}
      </span>
      {/* Portaled dropdown — same reason as the LogType chip: escapes
          ancestor stacking contexts so it renders above the results table. */}
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: coords.top, left: coords.left, minWidth: coords.width }}
          className="fixed z-[200] rounded-md bg-[#000414] border border-sky-900/40 shadow-2xl py-1"
          role="listbox"
        >
          {[
            { key: "log_date", label: "Log Date" },
            { key: "index_date", label: "Index Date" },
          ].map((opt) => {
            const selected = current === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
                className={`block w-full text-left px-3 py-1.5 text-sm transition-colors ${
                  selected
                    ? "text-sky-300 bg-sky-500/10"
                    : "text-neutral-200 hover:bg-white/[0.04]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ------------------- skeleton placeholders ------------------- */
function TableRowSkeleton() {
  return (
    <tr className="border-b border-sky-900/20">
      <td className="px-4 py-2.5"><div className="h-3 rounded bg-white/[0.04] animate-pulse w-12" /></td>
      <td className="px-4 py-2.5"><div className="h-3 rounded bg-white/[0.04] animate-pulse w-3/4" /></td>
      <td className="px-4 py-2.5"><div className="h-3 rounded bg-white/[0.04] animate-pulse w-2/3" /></td>
      <td className="px-4 py-2.5"><div className="h-4 rounded-md bg-white/[0.04] animate-pulse w-16" /></td>
      <td className="px-4 py-2.5"><div className="h-3 rounded bg-white/[0.04] animate-pulse w-20" /></td>
      <td className="px-4 py-2.5"><div className="h-3 rounded bg-white/[0.04] animate-pulse w-10 ml-auto" /></td>
    </tr>
  );
}

function MobileCardSkeleton() {
  return (
    <div className="rounded-md border border-sky-900/40 bg-[#0a1224]/90 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3 rounded bg-white/[0.06] animate-pulse w-16" />
          <div className="h-4 rounded bg-white/[0.06] animate-pulse w-3/4" />
          <div className="h-3 rounded bg-white/[0.06] animate-pulse w-1/2" />
        </div>
        <div className="h-7 rounded bg-white/[0.06] animate-pulse w-14 shrink-0" />
      </div>
    </div>
  );
}

/* ------------------- mobile cards for general results ------------------- */
function MobileResultCards({ rows, loading, err, hasQuery, onView, logType, onResetLogType }) {
  if (!rows || rows.length === 0) {
    if (loading) {
      return (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <MobileCardSkeleton key={i} />
          ))}
        </div>
      );
    }
    return (
      <div className="px-2 py-6 text-center text-neutral-400 text-base">
        {err ? (
          "Failed to load results."
        ) : !hasQuery ? (
          "No results yet. Try searching."
        ) : (
          <div className="space-y-2">
            <div>No results found.</div>
            {logType && logType !== "both" && onResetLogType && (
              <div className="text-[12px] text-neutral-500">
                Tip:{" "}
                <button
                  type="button"
                  onClick={onResetLogType}
                  className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline"
                >
                  switch Log Type to All
                </button>{" "}
                to broaden this search.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((r, idx) => (
        <div
          key={`${r.log_id}-${idx}`}
          className="rounded-md border border-sky-900/40 bg-[#0a1224]/90 p-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[12px] text-neutral-400 truncate" title={r.log_id}>
                #{r.log_id}
              </div>
              <div className="mt-0.5 text-[15px] font-medium text-neutral-100 truncate" title={r.username}>
                {r.username}
              </div>
              {r.applications && (
                <div className="mt-1 text-[13px] text-neutral-300 truncate" title={r.applications}>
                  {r.applications}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border ${
                    r.data_type === "stealer"
                      ? "bg-red-500/20 text-red-200 border-red-500/30"
                      : "bg-amber-500/20 text-amber-200 border-amber-500/30"
                  }`}
                >
                  {r.data_type}
                </span>
                <span className="text-[12px] text-neutral-400">{r.last_seen || "—"}</span>
              </div>
            </div>

            <div className="shrink-0">
              <button
                onClick={() => onView?.(r)}
                className="rounded-lg border border-sky-600/40 bg-transparent px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-500/10"
                aria-label="View details"
              >
                View
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------- Attack Surface (desktop table) ------------------- */
function AttackSurfaceTable({ rows, loading, err, hasQuery, onView }) {
  return (
    <table className="w-full">
      <thead className="bg-[#0a1224]/90 text-neutral-300">
        <tr className="border-b border-sky-900/40">
          <th className="px-4 py-3 text-left text-sm font-semibold w-24">Id</th>
          <th className="px-4 py-3 text-left text-sm font-semibold">Application</th>
          <th className="px-4 py-3 text-left text-sm font-semibold">Match Condition</th>
          <th className="px-4 py-3 text-left text-sm font-semibold w-28">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-800">
        {!rows || rows.length === 0 ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-neutral-400 text-base">
              {loading
                ? "Loading…"
                : err
                ? "Failed to load results."
                : hasQuery
                ? "No results found."
                : "No results yet. Try searching."}
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={r.id} className="hover:bg-white/5 transition">
              <td
                className="px-4 py-4 align-middle text-neutral-400 text-base truncate"
                title={r.id}
              >
                {r.id}
              </td>
              <td
                className="px-4 py-4 align-middle text-neutral-100 text-base truncate"
                title={r.application}
              >
                {r.application}
              </td>
              <td
                className="px-4 py-4 align-middle text-neutral-200 text-base truncate"
                title={r.data_type /* match_condition */}
              >
                {r.data_type}
              </td>
              <td className="px-4 py-4 align-middle">
                <button
                  onClick={() => onView?.(r)}
                  className="rounded-lg border border-sky-600/40 bg-transparent px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-500/10"
                >
                  View
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

/* ------------------- Attack Surface (mobile cards) ------------------- */
function AttackSurfaceCards({ rows, loading, err, hasQuery, onView }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="px-2 py-6 text-center text-neutral-400 text-base">
        {loading
          ? "Loading…"
          : err
          ? "Failed to load results."
          : hasQuery
          ? "No results found."
          : "No results yet. Try searching."}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div
          key={r.id}
          className="rounded-md border border-sky-900/40 bg-[#0a1224]/90 p-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[12px] text-neutral-400 truncate" title={r.id}>
                #{r.id}
              </div>
              <div
                className="mt-0.5 text-[15px] font-medium text-neutral-100 truncate"
                title={r.application}
              >
                {r.application}
              </div>
              <div
                className="mt-1 text-[13px] text-neutral-300 truncate"
                title={r.data_type /* match_condition */}
              >
                {r.data_type}
              </div>
            </div>

            <div className="shrink-0">
              <button
                onClick={() => onView?.(r)}
                className="rounded-lg border border-sky-600/40 bg-transparent px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-500/10"
                aria-label="View details"
              >
                View
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
